import { ReactNode } from 'react';
import { useTheme } from '@material-ui/core/styles';

/**
 * Small status/category pills shared by the catalog, API and docs tables so
 * every table speaks the same visual language as the GitHub Actions tab
 * (see modules/github-actions). Colors are tuned per theme for the SAVR
 * palette (brand #8F93FF/#4E4CCD, error #FF385F/#E11D48).
 */

export type PillTone = 'success' | 'warning' | 'error' | 'neutral' | 'brand';

const TONES: Record<PillTone, { dark: string; light: string }> = {
  success: { dark: '#34D399', light: '#059669' },
  warning: { dark: '#F5A623', light: '#D97706' },
  error: { dark: '#FF385F', light: '#E11D48' },
  neutral: { dark: '#8B8B93', light: '#6B7280' },
  brand: { dark: '#8F93FF', light: '#4E4CCD' },
};

export function Pill({
  label,
  tone = 'neutral',
  dot = false,
}: {
  label: ReactNode;
  tone?: PillTone;
  dot?: boolean;
}) {
  const theme = useTheme();
  const dark = theme.palette.type === 'dark';
  const color = dark ? TONES[tone].dark : TONES[tone].light;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 999,
        padding: '2px 10px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        color,
        backgroundColor: `${color}${dark ? '1f' : '14'}`,
        border: `1px solid ${color}4d`,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: color,
          }}
        />
      )}
      {label}
    </span>
  );
}

const LIFECYCLE_TONES: Record<string, PillTone> = {
  production: 'success',
  experimental: 'warning',
  deprecated: 'error',
};

export function LifecyclePill({ lifecycle }: { lifecycle?: unknown }) {
  if (typeof lifecycle !== 'string' || !lifecycle) {
    return null;
  }
  const tone = LIFECYCLE_TONES[lifecycle.toLocaleLowerCase('en-US')] ?? 'neutral';
  return <Pill label={lifecycle} tone={tone} dot />;
}

export function TypePill({ type }: { type?: unknown }) {
  if (typeof type !== 'string' || !type) {
    return null;
  }
  return <Pill label={type} tone="brand" />;
}
